import api from "./api";

// Lista todos os documentos enviados pelo usuário para um questionário específico
// Endpoint: GET /api/documento/GetAllDocumentoByIdQuestionarioResposta/{idResposta}
export const getDocumentosByRespostaId = async (idResposta) => {
  try {
    const response = await api.get(
      `/api/documento/GetAllDocumentoByIdQuestionarioResposta/${idResposta}`,
    );

    const data = response.data || [];

    const documentos = Array.isArray(data)
      ? data.map((doc) => ({
          id: doc.id,
          nomeArquivo: doc.nome || doc.nomeArquivo || doc.fileName || "",
          caminho: doc.caminho,
          identificador: doc.identificador,
          extensao: doc.extensao,
          dataUpload: doc.dataUpload,
          tamanhoEmBytes: doc.tamanhoEmBytes,
        }))
      : [];

    return documentos;
  } catch (error) {
    console.error("❌ Erro ao buscar documentos enviados:", error);
    return [];
  }
};

// Verifica se o arquivo pode ser aberto diretamente no navegador
const isRenderableInBrowser = (contentType = "") => {
  const t = contentType.toLowerCase();
  return (
    t.startsWith("application/pdf") ||
    t.startsWith("image/") ||
    t.startsWith("text/") ||
    t.startsWith("application/json")
  );
};

// Extrai o nome do arquivo a partir do header Content-Disposition
const getFilenameFromContentDisposition = (cd = "") => {
  if (!cd) return null;

  const fnStarMatch = cd.match(/filename\*\s*=\s*([^']*)''([^;]+)/i);
  if (fnStarMatch) {
    try {
      return decodeURIComponent(fnStarMatch[2]);
    } catch {
      return fnStarMatch[2];
    }
  }

  const fnMatch = cd.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  if (fnMatch)
    return (fnMatch[1] || fnMatch[2] || "").trim().replace(/^"(.*)"$/, "$1");

  return null;
};

// Infere a extensão do arquivo com base no content-type
const guessExtensionFromContentType = (ct = "") => {
  const map = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-excel": "xls",
    "application/pdf": "pdf",
    "application/zip": "zip",
    "application/json": "json",
    "text/plain": "txt",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
  };

  const norm = ct.toLowerCase().split(";")[0].trim();
  return map[norm] || "";
};

// Garante que o nome do arquivo tenha extensão válida
const ensureFilenameWithExt = (baseName = "arquivo", contentType = "") => {
  if (/\.[a-z0-9]+$/i.test(baseName)) return baseName;
  const ext = guessExtensionFromContentType(contentType);
  return ext ? `${baseName}.${ext}` : baseName;
};

// Faz download ou abre no navegador um documento pelo identificador
// Endpoint: GET /api/documento/downloadDocumentoQuestionarioRespostaByIdentificador/{identificador}
export const downloadDocumentoByIdentificador = async (
  identificador,
  nomeArquivoComExtensao = "arquivo",
) => {
  try {
    const response = await api.get(
      `/api/documento/downloadDocumentoQuestionarioRespostaByIdentificador/${identificador}`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  } catch (error) {
    console.error("❌ Erro ao obter documento:", error);
  }
};

// Remove um documento pelo identificador
// Endpoint: DELETE /api/documento/delete/{identificador}
export const deleteDocumentoByIdentificador = async (identificador) => {
  try {
    await api.delete(`/api/documento/delete/${identificador}`);
  } catch (error) {
    console.error("❌ Erro ao deletar documento:", error);
    throw error;
  }
};
